/**
 * Audio playback: decodes base64 PCM 24kHz, slows it down, and plays via AudioContext.
 * Non-blocking — schedules audio buffers sequentially.
 */

const OUTPUT_SAMPLE_RATE = 24000;

// Playback speed: 1.0 = normal, 0.85 = 15% slower, 0.75 = 25% slower
const PLAYBACK_RATE = 0.82;

export class AudioPlayer {
  constructor() {
    this._ctx = null;
    this._nextStartTime = 0;
  }

  _ensureContext() {
    if (!this._ctx) {
      this._ctx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
    }
    if (this._ctx.state === "suspended") {
      this._ctx.resume();
    }
  }

  /**
   * Queue a base64-encoded PCM chunk for playback.
   * @param {string} base64Data — base64-encoded raw 16-bit PCM at 24kHz
   */
  play(base64Data) {
    this._ensureContext();

    // Decode base64 → ArrayBuffer → Int16Array → Float32Array
    const binaryStr = atob(base64Data);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768;
    }

    // Create AudioBuffer
    const audioBuffer = this._ctx.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
    audioBuffer.getChannelData(0).set(float32);

    // Schedule playback at reduced speed
    const source = this._ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.playbackRate.value = PLAYBACK_RATE;
    source.connect(this._ctx.destination);

    const now = this._ctx.currentTime;
    const startTime = Math.max(now, this._nextStartTime);
    source.start(startTime);
    // Duration is longer when slowed down
    this._nextStartTime = startTime + audioBuffer.duration / PLAYBACK_RATE;
  }

  /** Stop all queued audio. */
  stop() {
    if (this._ctx) {
      this._ctx.close();
      this._ctx = null;
    }
    this._nextStartTime = 0;
  }
}
